import { Badge, Flex, IconButton, Table, Text, Tooltip } from '@radix-ui/themes'
import { Pencil1Icon } from '@radix-ui/react-icons'
import { LoggerMakerBadge } from '~/features/installations/components/LoggerMakerBadge'
import { useMeterMakerCatalog } from '~/features/installations/useMeterMakers'
import type { InstallationProfileRow } from '../assembly'
import { AssemblyProfileCell } from './AssemblyProfileCell'

interface InstallationProfilesTableProps {
  profiles: readonly InstallationProfileRow[]
  onEdit: (profile: InstallationProfileRow) => void
}

export const InstallationProfilesTable = ({ profiles, onEdit }: InstallationProfilesTableProps) => {
  const meterMakerLabel = useMeterMakerCatalog().labelOf

  return (
    <Table.Root variant="surface">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>Slug</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Meter makers</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Meter models</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Logger maker</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Assembly profile</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Supported</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell />
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {profiles.map((profile) => (
          <Table.Row key={profile.slug}>
            <Table.Cell>
              <Text size="2" weight="medium" className="font-mono">
                {profile.slug}
              </Text>
            </Table.Cell>
            <Table.Cell>
              <Flex gap="1" wrap="wrap">
                {profile.meter_makers.length > 0 ? (
                  profile.meter_makers.map((maker) => (
                    <Badge key={maker} color="gray" variant="soft">
                      {meterMakerLabel(maker)}
                    </Badge>
                  ))
                ) : (
                  <Text size="1" color="gray">
                    Unknown
                  </Text>
                )}
              </Flex>
            </Table.Cell>
            <Table.Cell>
              <Flex gap="1" wrap="wrap">
                {profile.meter_models.length > 0 ? (
                  profile.meter_models.map((model) => (
                    <Badge key={model} color="gray" variant="soft">
                      {model}
                    </Badge>
                  ))
                ) : (
                  <Text size="1" color="gray">
                    All
                  </Text>
                )}
              </Flex>
            </Table.Cell>
            <Table.Cell>
              {profile.logger_makers[0] ? (
                <LoggerMakerBadge loggerMaker={profile.logger_makers[0]} color="gray" />
              ) : (
                <Text size="1" color="gray">
                  Unknown
                </Text>
              )}
            </Table.Cell>
            <Table.Cell>
              <AssemblyProfileCell profile={profile} />
            </Table.Cell>
            <Table.Cell>
              {profile.supported ? (
                <Badge color="green" variant="soft">
                  Supported
                </Badge>
              ) : (
                <Badge color="gray" variant="soft">
                  Not supported
                </Badge>
              )}
            </Table.Cell>
            <Table.Cell>
              <Flex justify="end">
                <Tooltip content="Edit profile">
                  <IconButton variant="soft" color="gray" size="1" onClick={() => onEdit(profile)}>
                    <Pencil1Icon />
                  </IconButton>
                </Tooltip>
              </Flex>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )
}
